import React, { useCallback, useState, createContext, useContext } from 'react';
type Reaction = 'like' | 'dislike' | null;
interface ReactionCounts {
  likes: number;
  dislikes: number;
}
interface ReactionsContextType {
  reactions: Record<string, Reaction>;
  getReaction: (toolId: string) => Reaction;
  setReaction: (toolId: string, reaction: Reaction) => void;
  getCounts: (toolId: string) => ReactionCounts;
}
const STORAGE_KEY = '4all-tool-reactions';
const COUNTS_STORAGE_KEY = '4all-tool-reaction-counts';
function loadReactions(): Record<string, Reaction> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}
function saveReactions(reactions: Record<string, Reaction>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reactions));
  } catch {
    // localStorage unavailable
  }
}
function loadCounts(): Record<string, ReactionCounts> {
  try {
    const stored = localStorage.getItem(COUNTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}
function saveCounts(counts: Record<string, ReactionCounts>) {
  try {
    localStorage.setItem(COUNTS_STORAGE_KEY, JSON.stringify(counts));
  } catch {
    // localStorage unavailable
  }
}
const ReactionsContext = createContext<ReactionsContextType | undefined>(undefined);
export function ReactionsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [reactions, setReactions] = useState<Record<string, Reaction>>(loadReactions);
  const [counts, setCounts] = useState<Record<string, ReactionCounts>>(loadCounts);
  const getReaction = useCallback((toolId: string): Reaction => reactions[toolId] ?? null, [reactions]);
  const getCounts = useCallback((toolId: string): ReactionCounts => counts[toolId] ?? {
    likes: 0,
    dislikes: 0
  }, [counts]);
  const setReaction = useCallback((toolId: string, reaction: Reaction) => {
    setReactions(prev => {
      const oldReaction = prev[toolId] ?? null;
      const next = {
        ...prev
      };
      if (reaction === null) {
        delete next[toolId];
      } else {
        next[toolId] = reaction;
      }
      saveReactions(next);
      // Update counts based on the transition
      setCounts(prevCounts => {
        const current = prevCounts[toolId] ?? {
          likes: 0,
          dislikes: 0
        };
        const updated = {
          ...current
        };
        // Remove old reaction count
        if (oldReaction === 'like') {
          updated.likes = Math.max(0, updated.likes - 1);
        } else if (oldReaction === 'dislike') {
          updated.dislikes = Math.max(0, updated.dislikes - 1);
        }
        // Add new reaction count
        if (reaction === 'like') {
          updated.likes += 1;
        } else if (reaction === 'dislike') {
          updated.dislikes += 1;
        }
        const nextCounts = {
          ...prevCounts,
          [toolId]: updated
        };
        saveCounts(nextCounts);
        return nextCounts;
      });
      return next;
    });
  }, []);
  return <ReactionsContext.Provider value={{
    reactions,
    getReaction,
    setReaction,
    getCounts
  }} data-id="element-161">
      {children}
    </ReactionsContext.Provider>;
}
export function useReactions() {
  const context = useContext(ReactionsContext);
  if (!context) {
    throw new Error('useReactions must be used within a ReactionsProvider');
  }
  return context;
}